import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useLocation, Link } from 'react-router-dom';
import { Plus, Search, UserCog, Mail, Phone, Edit, Trash2, Eye, AlertCircle, AlertTriangle, RefreshCw, Clock, FileCheck, XCircle, Filter, ChevronDown, Users, GraduationCap, Building2, ClipboardCheck, X, FileEdit, UserCheck, Loader2, Shield, Calendar, Archive, ArchiveRestore, Key, KeyRound, Copy, EyeOff, CheckCircle, Ban, UserPlus, FolderOpen, MoreVertical, Hourglass } from 'lucide-react';
import { ResetDeadlineModal } from '@/shared/components/users/ResetDeadlineModal';
import {
  fetchStaffUsers,
  fetchManagementPermissions,
  fetchStaffStats,
  resendVerificationEmail,
  resetProfileDeadline,
  deleteStaff,
  archiveStaff,
  restoreStaff,
  adminResetPassword,
  adminResetStaffTempPassword,
  suspendStaff,
  unsuspendStaff,
  getStaffSuspensionInfo,
  type StaffUser,
  type ManagementPermissions,
  type StaffStats,
  type StaffFilters as ApiStaffFilters,
  type AdminResetStaffTempPasswordResponse,
  type StaffSuspensionInfo,
  getStaffCountsByAcademicYear,
} from '@/services/userService';
import { getPendingValidations, getValidationRequestByUserId, type PendingValidation } from '@/services/validationService';
import { getPendingChangeRequests, type ChangeRequest } from '@/services/changeRequestService';
import { fetchAcademicYears, type AcademicYear } from '@/services/academicPlanningService';
import { fetchCampuses } from '@/services/etablissementService';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';
import { canValidateRole } from '@/utils/validationHierarchy';
import { CreateStaffModal } from '@/shared/components/users/CreateStaffModal';
import { StaffPermissionsModal } from '@/shared/components/users/StaffPermissionsModal';
import { ViewStaffModal } from '@/shared/components/users/ViewStaffModal';
import { ProfileValidationModal } from '@/shared/components/users/ProfileValidationModal';
import { StaffFiltersPanel, type StaffFilters } from '@/shared/components/users/StaffFiltersPanel';
import { AdminChangeRequestsCard } from '@/shared/components/users/AdminChangeRequestsCard';
import { DirectEditStaffProfileModal } from '@/shared/components/users/DirectEditStaffProfileModal';
import { PhysicalDocumentsTrackingModal } from '@/shared/components/users/PhysicalDocumentsTrackingModal';
import { formatPhoneWithCountry } from '@/shared/constants/phoneCodes';
import { buildHaystack, matchesAllTokens } from '@/shared/utils/searchMatch';

const statusLabels: Record<string, { label: string; color: string }> = {
  ACTIVE: { label: 'Actif', color: 'bg-green-100 text-green-800' },
  PENDING: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
  INACTIVE: { label: 'Inactif', color: 'bg-gray-100 text-gray-800' },
  SUSPENDED: { label: 'Suspendu', color: 'bg-red-100 text-red-800' },
  WITHDRAWN: { label: 'Archivé', color: 'bg-orange-100 text-orange-800' },
};

const profileStatusLabels: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  DRAFT: { label: 'Brouillon', color: 'bg-gray-100 text-gray-700', icon: Edit },
  PENDING_VALIDATION: { label: 'À valider', color: 'bg-orange-100 text-orange-800', icon: Clock },
  APPROVED: { label: 'Validé', color: 'bg-green-100 text-green-800', icon: FileCheck },
  REJECTED: { label: 'Rejeté', color: 'bg-red-100 text-red-800', icon: XCircle },
};

type ProfileStatusFilter = 'ALL' | 'PENDING_VALIDATION' | 'DRAFT' | 'APPROVED' | 'REJECTED';
type RoleFilter = 'ALL' | 'TEACHERS' | string; // 'TEACHERS' for professors, or specific role

export const StaffManagementPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tokens = useAuthStore((state) => state.tokens);
  const currentUser = useAuthStore((state) => state.user);
  const fetchPermissions = useAuthStore((state) => state.fetchPermissions);
  const { hasPermission } = usePermissions();

  // Refs for race condition prevention
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Refresh permissions from API when the page loads to ensure we have the latest
  useEffect(() => {
    fetchPermissions().catch(() => {
      // Silently fail - will use cached permissions
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch campus count to gate staff creation when no campuses exist yet
  useEffect(() => {
    fetchCampuses(undefined, true)
      .then((list) => setCampusCount(list.length))
      .catch(() => setCampusCount(null));
  }, []);

  // Reset mounted ref on mount and cleanup on unmount
  // This fixes React 18 StrictMode which unmounts/remounts components
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Permission checks
  const canManagePermissions = hasPermission(Permission.PERMISSION_MANAGE);
  const canValidateProfile = hasPermission(Permission.STAFF_VALIDATE_PROFILE);
  const canValidateModifications = hasPermission(Permission.STAFF_VALIDATE_MODIFICATIONS);
  const canEditProfile = hasPermission(Permission.STAFF_EDIT_PROFILE);
  const canDeleteStaff = hasPermission(Permission.STAFF_DELETE);
  const canArchiveStaff = hasPermission(Permission.STAFF_ARCHIVE);
  const canResetPassword = hasPermission(Permission.STAFF_RESET_PASSWORD);
  const canSuspendActivate = hasPermission(Permission.STAFF_SUSPEND_ACTIVATE);
  const canResendActivation = hasPermission(Permission.STAFF_RESEND_ACTIVATION);
  const canTrackPhysicalDocs = hasPermission(Permission.TRACKING_PHYSICAL_DOCS_VIEW);
  const [staff, setStaff] = useState<StaffUser[]>([]);
  const [permissions, setPermissions] = useState<ManagementPermissions | null>(null);
  const [stats, setStats] = useState<StaffStats | null>(null);
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  // Headcount per year, counting what the list actually shows.
  const [academicYearCounts, setAcademicYearCounts] = useState<Record<string, number>>({});
  const [campusCount, setCampusCount] = useState<number | null>(null);
  const location = useLocation();
  const campusManagementPath = location.pathname.replace(/\/utilisateurs\/personnel.*$/, '/etablissement/gestion');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const initialLoadDone = React.useRef(false);
  const allStaffCachedRef = React.useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<StaffUser | null>(null);
  const [profileStatusFilter, setProfileStatusFilter] = useState<ProfileStatusFilter>('ALL');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('ALL');

  // Advanced filters state (checkbox-based multi-select)
  const [advancedFilters, setAdvancedFilters] = useState<StaffFilters>({});
  const [allStaff, setAllStaff] = useState<StaffUser[]>([]); // Store unfiltered data for filter options

  // Server-side pagination + search
  const [page, setPage] = useState(1);
  const [totalStaffCount, setTotalStaffCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [serverSearch, setServerSearch] = useState('');
  const [currentAcademicYear, setCurrentAcademicYear] = useState<AcademicYear | null>(null);
  const [academicYearInitialized, setAcademicYearInitialized] = useState(false);

  // Track URL parameters
  const validationRequestId = searchParams.get('validation');
  const showChangeRequestsParam = searchParams.get('showChangeRequests');

  // Validation modal state
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [selectedValidationId, setSelectedValidationId] = useState<string | null>(null);
  const [pendingValidations, setPendingValidations] = useState<PendingValidation[]>([]);

  // Change requests state
  const [pendingChangeRequests, setPendingChangeRequests] = useState<ChangeRequest[]>([]);
  const [showChangeRequestsSection, setShowChangeRequestsSection] = useState(false);

  // Resend email states
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);

  // Permissions modal state
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedStaffForPermissions, setSelectedStaffForPermissions] = useState<StaffUser | null>(null);

  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState<StaffUser | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [staffToResetDeadline, setStaffToResetDeadline] = useState<StaffUser | null>(null);
  const [resettingDeadline, setResettingDeadline] = useState(false);

  // Archive confirmation modal
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [staffToArchive, setStaffToArchive] = useState<StaffUser | null>(null);
  const [archiving, setArchiving] = useState(false);

  // Show archived staff toggle
  const [showArchivedStaff, setShowArchivedStaff] = useState(false);

  // Suspension modal
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [staffToSuspend, setStaffToSuspend] = useState<StaffUser | null>(null);
  const [suspending, setSuspending] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState('');
  const [suspensionError, setSuspensionError] = useState<string | null>(null);
  const [suspensionInfo, setSuspensionInfo] = useState<StaffSuspensionInfo | null>(null);
  const [loadingSuspensionInfo, setLoadingSuspensionInfo] = useState(false);

  // Password reset modal - Step 1: Choose method (email or temp password)
  const [showResetPasswordChoiceModal, setShowResetPasswordChoiceModal] = useState(false);
  const [staffForPasswordReset, setStaffForPasswordReset] = useState<StaffUser | null>(null);

  // Password reset modal - Step 2: Email-based reset
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [staffToResetPassword, setStaffToResetPassword] = useState<StaffUser | null>(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState<string | null>(null);
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);

  // Password reset modal - Staff temp password
  const [showResetStaffTempPasswordModal, setShowResetStaffTempPasswordModal] = useState(false);
  const [staffTempResetResult, setStaffTempResetResult] = useState<AdminResetStaffTempPasswordResponse | null>(null);
  const [resettingStaffTempPassword, setResettingStaffTempPassword] = useState(false);
  const [resetStaffTempPasswordError, setResetStaffTempPasswordError] = useState<string | null>(null);
  const [showStaffTempPassword, setShowStaffTempPassword] = useState(false);
  const [copiedStaffField, setCopiedStaffField] = useState<'email' | 'password' | null>(null);

  // Edit profile modal state
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [staffToEdit, setStaffToEdit] = useState<StaffUser | null>(null);

  // Physical documents tracking modal state
  const [showPhysicalDocsModal, setShowPhysicalDocsModal] = useState(false);
  const [staffForPhysicalDocs, setStaffForPhysicalDocs] = useState<StaffUser | null>(null);

  // Actions dropdown state
  const [openActionsDropdown, setOpenActionsDropdown] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; showAbove: boolean }>({ top: 0, left: 0, showAbove: false });
  const actionsDropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  // Calculate dropdown position when opening
  const handleOpenDropdown = useCallback((memberId: string, buttonElement: HTMLButtonElement) => {
    if (openActionsDropdown === memberId) {
      setOpenActionsDropdown(null);
      return;
    }

    const rect = buttonElement.getBoundingClientRect();
    const dropdownHeight = 350; // Approximate max dropdown height
    const spaceBelow = window.innerHeight - rect.bottom;
    const showAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

    setDropdownPosition({
      top: showAbove ? rect.top : rect.bottom + 4,
      left: rect.right - 224, // 224px = dropdown width (w-56 = 14rem = 224px)
      showAbove,
    });
    setOpenActionsDropdown(memberId);
  }, [openActionsDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target as Node)) {
        // Check if click was on a dropdown button
        const target = event.target as HTMLElement;
        if (!target.closest('[data-dropdown-trigger]')) {
          setOpenActionsDropdown(null);
        }
      }
    };

    const handleScroll = () => {
      setOpenActionsDropdown(null);
    };

    if (openActionsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [openActionsDropdown]);

  // Load current academic year and set as default filter
  // Use tokens from hook and re-run when tokens become available
  useEffect(() => {
    // Skip if already initialized or no tokens yet
    if (academicYearInitialized) return;
    if (!tokens?.accessToken) return;

    const loadCurrentAcademicYear = async () => {
      try {
        const years = await fetchAcademicYears(tokens.accessToken);
        if (!isMountedRef.current) return;

        setAcademicYears(years);

        // Per-year counts come from the server: they follow the filter's own
        // rule (active staff always listed, the year only scopes archived ones).
        getStaffCountsByAcademicYear()
          .then((rows) =>
            setAcademicYearCounts(
              Object.fromEntries(rows.map((r) => [r.academicYearId, r.count])),
            ),
          )
          .catch(() => undefined);
        const current = years.find(y => y.isCurrent);
        if (current) {
          setCurrentAcademicYear(current);
          // Pre-select current academic year in filters
          setAdvancedFilters(prev => ({
            ...prev,
            academicYearId: [current.id]
          }));
        }
        setAcademicYearInitialized(true);
      } catch (error) {
        console.error('Error loading academic years:', error);
        if (isMountedRef.current) {
          setAcademicYearInitialized(true);
        }
      }
    };

    loadCurrentAcademicYear();
  }, [tokens?.accessToken, academicYearInitialized]);

  // Load data after academic year is initialized
  useEffect(() => {
    if (academicYearInitialized) {
      loadData(currentAcademicYear ? true : false);
    }
  }, [academicYearInitialized]);

  // Auto-filter to pending validation when coming from a notification
  useEffect(() => {
    if (validationRequestId) {
      setProfileStatusFilter('PENDING_VALIDATION');
      // Clear the validation param from URL after applying filter
      setSearchParams({}, { replace: true });
    }
  }, [validationRequestId, setSearchParams]);

  // Auto-show change requests section when coming from a notification
  useEffect(() => {
    if (showChangeRequestsParam === 'true') {
      setShowChangeRequestsSection(true);
      // Clear the param from URL after applying
      setSearchParams({}, { replace: true });
    }
  }, [showChangeRequestsParam, setSearchParams]);

  const loadData = async (applyFilters?: boolean) => {
    // When applyFilters isn't explicitly passed (e.g. reloads triggered after a
    // profile edit/validation/save), preserve any active advanced filter instead
    // of silently reverting to the full list.
    const shouldApply = applyFilters ?? (
      (advancedFilters.role?.length ?? 0) > 0 ||
      (advancedFilters.academicYearId?.length ?? 0) > 0 ||
      (advancedFilters.campusId?.length ?? 0) > 0
    );

    // Cancel any previous in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    try {
      // Full-page spinner only on the very first load; afterwards keep the
      // table on screen and show a lightweight "searching" indicator instead.
      if (!initialLoadDone.current) {
        setLoading(true);
      } else {
        setSearching(true);
      }
      setError(null);

      // Prepare filters for API call (always paginated server-side)
      const filtersToApply: ApiStaffFilters = {
        ...(shouldApply ? advancedFilters : {}),
        ...(serverSearch ? { search: serverSearch } : {}),
        page,
        limit: 50,
      };

      // The unfiltered fetch feeds filter options/exports and does not depend on
      // the search term — skip it while typing once a cached copy exists.
      const needAllStaff = shouldApply && (!allStaffCachedRef.current || !serverSearch);

      const [staffResponse, allStaffResponse, permissionsData, statsData, validationsData, changeRequestsData] = await Promise.all([
        fetchStaffUsers(filtersToApply).catch(err => {
          if (err.name === 'AbortError') throw err;
          throw err;
        }),
        needAllStaff ? fetchStaffUsers().catch(err => {
          if (err.name === 'AbortError') throw err;
          throw err;
        }) : Promise.resolve({ data: [], meta: { page: 1, limit: 50, total: 0, totalPages: 0 } }), // Fetch all staff for filter options
        fetchManagementPermissions().catch(err => {
          if (err.name === 'AbortError') throw err;
          throw err;
        }),
        fetchStaffStats().catch(err => {
          if (err.name === 'AbortError') throw err;
          return null;
        }), // Don't fail if stats can't be loaded
        getPendingValidations().catch(err => {
          if (err.name === 'AbortError') throw err;
          return [];
        }), // Don't fail if validations can't be loaded
        getPendingChangeRequests().catch(err => {
          if (err.name === 'AbortError') throw err;
          return [];
        }), // Don't fail if change requests can't be loaded
      ]);

      // Check if request was aborted or component unmounted before setting state
      if (signal.aborted || !isMountedRef.current) return;

      // Extract data arrays from paginated responses
      const staffData = staffResponse.data;
      const allStaffData = allStaffResponse.data;

      setStaff(staffData);
      setTotalStaffCount(staffResponse?.meta?.total ?? staffData.length);
      setTotalPages(staffResponse?.meta?.totalPages ?? 1);
      setPermissions(permissionsData);
      setStats(statsData);

      // Store unfiltered data for filter options
      if (shouldApply && allStaffData.length > 0) {
        setAllStaff(allStaffData);
        allStaffCachedRef.current = true;
      } else if (!shouldApply) {
        setAllStaff(staffData);
      }
      initialLoadDone.current = true;

      // Filter validations to only include staff (non-student, non-parent)
      const staffValidations = validationsData.filter((v: PendingValidation) =>
        v.user.role !== 'STUDENT' && v.user.role !== 'PARENT'
      );
      setPendingValidations(staffValidations);

      // Filter change requests to only include staff (non-student, non-parent)
      const staffChangeRequests = changeRequestsData.filter((r: ChangeRequest) =>
        r.user?.role !== 'STUDENT' && r.user?.role !== 'PARENT'
      );
      setPendingChangeRequests(staffChangeRequests);
    } catch (err) {
      // Ignore aborted requests
      if (err instanceof Error && err.name === 'AbortError') return;
      if (!isMountedRef.current) return;

      console.error('Error fetching staff data:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des données');
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setSearching(false);
      }
    }
  };

  // Find validation request ID for a staff member
  const getValidationRequestId = (userId: string): string | null => {
    const validation = pendingValidations.find(v => v.userId === userId);
    return validation?.id || null;
  };

  // Handle opening validation modal
  const handleOpenValidation = async (userId: string) => {
    // First try from cache
    let validationId = getValidationRequestId(userId);

    // If not in cache, fetch from API
    if (!validationId) {
      try {
        validationId = await getValidationRequestByUserId(userId);
      } catch (err) {
        console.error('Error fetching validation request:', err);
      }
    }

    if (validationId) {
      setSelectedValidationId(validationId);
      setShowValidationModal(true);
    } else {
      console.error('No validation request found for user:', userId);
    }
  };

  // Handle validation complete
  const handleValidationComplete = () => {
    loadData(); // Refresh to update statuses
  };

  // Handle resend verification email
  const handleResendEmail = async (userId: string) => {
    setResendingUserId(userId);
    setResendSuccess(null);
    setResendError(null);

    try {
      const result = await resendVerificationEmail(userId);
      setResendSuccess(result.message);
      // Clear success message after 5 seconds
      setTimeout(() => setResendSuccess(null), 5000);
    } catch (error) {
      setResendError(error instanceof Error ? error.message : 'Erreur lors de l\'envoi');
      // Clear error message after 5 seconds
      setTimeout(() => setResendError(null), 5000);
    } finally {
      setResendingUserId(null);
    }
  };

  // Debounce the search input into a server-side search term
  useEffect(() => {
    const timer = setTimeout(() => setServerSearch(searchQuery.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reload when filters or search change (back to page 1), or when page changes
  useEffect(() => {
    // Don't trigger reload until initial data load is complete
    if (!academicYearInitialized) return;
    if (page !== 1) {
      setPage(1); // triggers the page effect below, which reloads
      return;
    }
    loadData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, serverSearch, academicYearInitialized]);

  useEffect(() => {
    if (!academicYearInitialized) return;
    loadData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Build role names map from permissions
  const roleNamesMap = useMemo(() => {
    const map = new Map<string, string>();
    if (permissions?.creatableRoles) {
      permissions.creatableRoles.forEach((r) => {
        map.set(r.role, r.nameFr);
      });
    }
    // Add current role info as well
    if (permissions?.currentRole && permissions?.currentRoleInfo) {
      map.set(permissions.currentRole, permissions.currentRoleInfo.nameFr);
    }
    return map;
  }, [permissions]);

  const filteredStaff = useMemo(() => {
    return staff.filter(s => {
      // Filter archived staff - hide by default, show when checkbox is checked (include both active and archived)
      if (!showArchivedStaff && s.status === 'WITHDRAWN') {
        return false;
      }
      // When showing archived, show ALL staff (both active and archived)

      // Filter by profile status
      if (profileStatusFilter !== 'ALL') {
        if (s.profileSubmissionStatus !== profileStatusFilter) {
          return false;
        }
      }

      // Filter by role
      if (roleFilter !== 'ALL') {
        if (roleFilter === 'TEACHERS') {
          // Show only teachers (TEACHER role or staff who are also professors)
          const isTeacher = s.role === 'TEACHER';
          const isAlsoProfessor = s.isAlsoProfessor === true;
          if (!isTeacher && !isAlsoProfessor) {
            return false;
          }
        } else {
          // Specific role filter
          if (s.role !== roleFilter) {
            return false;
          }
        }
      }

      // Then filter by search — token-based, same rule as the backend (every
      // word must match), so "Prénom Nom", "Nom Prénom", trailing spaces and
      // mixed case all work and never contradict the server results.
      const haystack = buildHaystack([s.firstName, s.lastName, s.email, s.staffCode]);
      return matchesAllTokens(searchQuery, haystack);
    })
    // Always display alphabetically (by last name, then first name).
    .sort((a, b) => {
      const byLast = (a.lastName || '').localeCompare(b.lastName || '', 'fr', { sensitivity: 'base' });
      return byLast !== 0
        ? byLast
        : (a.firstName || '').localeCompare(b.firstName || '', 'fr', { sensitivity: 'base' });
    });
  }, [staff, searchQuery, profileStatusFilter, roleFilter, showArchivedStaff]);

  // Count archived staff separately
  const archivedCount = useMemo(() => {
    return staff.filter(s => s.status === 'WITHDRAWN').length;
  }, [staff]);

  // Count pending validations (use stats if available, otherwise calculate)
  const pendingValidationCount = stats?.pendingValidation ??
    staff.filter(s => s.profileSubmissionStatus === 'PENDING_VALIDATION').length;

  const getRoleName = (role: string): string => {
    const roleInfo = permissions?.creatableRoles.find(r => r.role === role);
    return roleInfo?.nameFr || role;
  };

  // Filter creatable roles based on STAFF_CREATE permission (includes teachers)
  const creatableStaffRoles = useMemo(() => {
    if (!permissions?.creatableRoles) return [];

    return permissions.creatableRoles.filter(r => {
      // Exclude student, parent, and call center agent roles
      if (['STUDENT', 'PARENT', 'CALL_CENTER_AGENT'].includes(r.role)) return false;

      // STAFF_CREATE allows creating all staff including teachers
      return permissions.canCreateStaff;
    });
  }, [permissions]);

  const handleViewStaff = (member: StaffUser) => {
    setSelectedStaff(member);
    setShowViewModal(true);
  };

  const handleOpenPermissions = (member: StaffUser) => {
    setSelectedStaffForPermissions(member);
    setShowPermissionsModal(true);
  };

  const handleClosePermissions = () => {
    setShowPermissionsModal(false);
    setSelectedStaffForPermissions(null);
  };

  // Delete handlers
  const handleDeleteClick = (member: StaffUser) => {
    setStaffToDelete(member);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!staffToDelete) return;

    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteStaff(staffToDelete.id);
      setShowDeleteModal(false);
      setStaffToDelete(null);
      loadData(); // Refresh the list
    } catch (error) {
      console.error('Error deleting staff:', error);
      setDeleteError(error instanceof Error ? error.message : 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setStaffToDelete(null);
    setDeleteError(null);
  };

  const handleConfirmResetDeadline = async () => {
    if (!staffToResetDeadline) return;
    setResettingDeadline(true);
    setResendSuccess(null);
    setResendError(null);
    try {
      const status = await resetProfileDeadline(staffToResetDeadline.id);
      const until = status.deadlineDate ? new Date(status.deadlineDate).toLocaleDateString('fr-FR') : null;
      setResendSuccess(
        `Délai de soumission réinitialisé pour ${staffToResetDeadline.firstName} ${staffToResetDeadline.lastName}${until ? ` (jusqu'au ${until})` : ''}.`,
      );
      setStaffToResetDeadline(null);
      loadData();
      setTimeout(() => setResendSuccess(null), 6000);
    } catch (error) {
      setResendError(error instanceof Error ? error.message : 'Erreur lors de la réinitialisation du délai');
      setTimeout(() => setResendError(null), 6000);
    } finally {
      setResettingDeadline(false);
    }
  };

  // Archive handlers
  const handleArchiveClick = (member: StaffUser) => {
    setStaffToArchive(member);
    setShowArchiveModal(true);
  };

  const handleConfirmArchive = async () => {
    if (!staffToArchive) return;

    setArchiving(true);
    const isRestoring = staffToArchive.status === 'WITHDRAWN';

    try {
      if (isRestoring) {
        // Restore archived staff
        await restoreStaff(staffToArchive.id);
      } else {
        // Archive staff
        await archiveStaff(staffToArchive.id);
      }

      // Optimistically update local state immediately for better UX
      const newStatus = isRestoring ? 'ACTIVE' : 'WITHDRAWN';
      setStaff(prevStaff =>
        prevStaff.map(s =>
          s.id === staffToArchive.id ? { ...s, status: newStatus } : s
        )
      );
      setAllStaff(prevAllStaff =>
        prevAllStaff.map(s =>
          s.id === staffToArchive.id ? { ...s, status: newStatus } : s
        )
      );

      setShowArchiveModal(false);
      setStaffToArchive(null);

      // Refresh stats in background to update counts
      loadData();
    } catch (error) {
      console.error('Error archiving/restoring staff:', error);
    } finally {
      setArchiving(false);
    }
  };

  const handleCancelArchive = () => {
    setShowArchiveModal(false);
    setStaffToArchive(null);
  };

  // Edit profile handlers
  const handleEditProfileClick = (member: StaffUser) => {
    setStaffToEdit(member);
    setShowEditProfileModal(true);
  };

  const handleEditProfileSuccess = () => {
    loadData(); // Refresh the list after successful edit
  };

  // Suspension handlers
  const handleSuspendClick = async (member: StaffUser) => {
    setStaffToSuspend(member);
    setSuspensionReason('');
    setSuspensionError(null);
    setSuspensionInfo(null);
    setShowSuspendModal(true);

    // If the staff member is suspended, load their suspension info
    if (member.status === 'SUSPENDED') {
      setLoadingSuspensionInfo(true);
      try {
        const info = await getStaffSuspensionInfo(member.id);
        setSuspensionInfo(info);
      } catch (error) {
        console.error('Error loading suspension info:', error);
      } finally {
        setLoadingSuspensionInfo(false);
      }
    }
  };

  const handleConfirmSuspend = async () => {
    if (!staffToSuspend) return;

    const isSuspended = staffToSuspend.status === 'SUSPENDED';

    // For suspension, reason is required
    if (!isSuspended && !suspensionReason.trim()) {
      setSuspensionError('La raison de suspension est obligatoire');
      return;
    }

    setSuspending(true);
    setSuspensionError(null);

    try {
      if (isSuspended) {
        // Unsuspend/activate the staff member
        await unsuspendStaff(staffToSuspend.id);
      } else {
        // Suspend the staff member
        await suspendStaff(staffToSuspend.id, suspensionReason.trim());
      }

      // Optimistically update local state
      const newStatus = isSuspended ? 'ACTIVE' : 'SUSPENDED';
      setStaff(prevStaff =>
        prevStaff.map(s =>
          s.id === staffToSuspend.id ? { ...s, status: newStatus } : s
        )
      );
      setAllStaff(prevAllStaff =>
        prevAllStaff.map(s =>
          s.id === staffToSuspend.id ? { ...s, status: newStatus } : s
        )
      );

      setShowSuspendModal(false);
      setStaffToSuspend(null);
      setSuspensionReason('');

      // Refresh data in background
      loadData();
    } catch (error) {
      console.error('Error suspending/activating staff:', error);
      setSuspensionError(error instanceof Error ? error.message : 'Une erreur est survenue');
    } finally {
      setSuspending(false);
    }
  };

  const handleCancelSuspend = () => {
    setShowSuspendModal(false);
    setStaffToSuspend(null);
    setSuspensionReason('');
    setSuspensionError(null);
    setSuspensionInfo(null);
  };

  // Password reset handlers - Show choice modal
  const handleResetPasswordClick = (member: StaffUser) => {
    setStaffForPasswordReset(member);
    setShowResetPasswordChoiceModal(true);
  };

  // Handle choice: reset staff password via email
  const handleChooseStaffEmailReset = () => {
    if (!staffForPasswordReset) return;
    setShowResetPasswordChoiceModal(false);
    setStaffToResetPassword(staffForPasswordReset);
    setShowResetPasswordModal(true);
    setResetPasswordSuccess(null);
    setResetPasswordError(null);
  };

  // Handle choice: reset staff password with temp password
  const handleChooseStaffTempPasswordReset = () => {
    if (!staffForPasswordReset) return;
    setShowResetPasswordChoiceModal(false);
    setStaffToResetPassword(staffForPasswordReset);
    setShowResetStaffTempPasswordModal(true);
    setStaffTempResetResult(null);
    setResetStaffTempPasswordError(null);
    setShowStaffTempPassword(false);
    setCopiedStaffField(null);
  };

  const handleCancelPasswordChoice = () => {
    setShowResetPasswordChoiceModal(false);
    setStaffForPasswordReset(null);
  };

  const handleConfirmResetPassword = async () => {
    if (!staffToResetPassword) return;

    setResettingPassword(true);
    setResetPasswordError(null);
    try {
      const result = await adminResetPassword(staffToResetPassword.id);
      setResetPasswordSuccess(result.message);
      // Close modal after a short delay to show success message
      setTimeout(() => {
        setShowResetPasswordModal(false);
        setStaffToResetPassword(null);
        setStaffForPasswordReset(null);
        setResetPasswordSuccess(null);
      }, 2000);
    } catch (error) {
      console.error('Error resetting password:', error);
      setResetPasswordError(error instanceof Error ? error.message : 'Erreur lors de la réinitialisation du mot de passe');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleCancelResetPassword = () => {
    setShowResetPasswordModal(false);
    setStaffToResetPassword(null);
    setStaffForPasswordReset(null);
    setResetPasswordSuccess(null);
    setResetPasswordError(null);
  };

  // Staff temp password reset handlers
  const handleConfirmResetStaffTempPassword = async () => {
    if (!staffToResetPassword) return;

    setResettingStaffTempPassword(true);
    setResetStaffTempPasswordError(null);
    try {
      const result = await adminResetStaffTempPassword(staffToResetPassword.id);
      setStaffTempResetResult(result);
    } catch (error) {
      console.error('Error resetting staff temp password:', error);
      setResetStaffTempPasswordError(error instanceof Error ? error.message : 'Erreur lors de la réinitialisation du mot de passe');
    } finally {
      setResettingStaffTempPassword(false);
    }
  };

  const handleCancelResetStaffTempPassword = () => {
    setShowResetStaffTempPasswordModal(false);
    setStaffToResetPassword(null);
    setStaffForPasswordReset(null);
    setStaffTempResetResult(null);
    setResetStaffTempPasswordError(null);
    setShowStaffTempPassword(false);
    setCopiedStaffField(null);
  };

  const copyStaffCredential = async (text: string, field: 'email' | 'password') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStaffField(field);
      setTimeout(() => setCopiedStaffField(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Check if we have multiple campuses
  const hasMultipleCampuses = useMemo(() => {
    const campusSet = new Set<string>();
    staff.forEach(s => {
      s.campuses?.forEach(c => campusSet.add(c.id));
    });
    return campusSet.size > 1;
  }, [staff]);

  // Clear all filters
  const clearFilters = () => {
    setProfileStatusFilter('ALL');
    setRoleFilter('ALL');
    setSearchQuery('');
    setAdvancedFilters({});
  };

  // Check if any filter is active
  const hasAdvancedFiltersActive =
    (advancedFilters.role && advancedFilters.role.length > 0) ||
    (advancedFilters.academicYearId && advancedFilters.academicYearId.length > 0) ||
    (advancedFilters.campusId && advancedFilters.campusId.length > 0);

  const hasActiveFilters = profileStatusFilter !== 'ALL' || roleFilter !== 'ALL' || hasAdvancedFiltersActive;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Show error state if API call failed
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-800 mb-4">{error}</p>
        <button
          onClick={() => loadData()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <RefreshCw size={16} />
          Réessayer
        </button>
      </div>
    );
  }

  // Show permissions error only if we have permissions data but user can't manage staff
  if (permissions && !permissions.canManageStaff) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
        <p className="text-yellow-800">
          Vous n'avez pas les permissions pour gérer le personnel.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion du Personnel</h1>
          <p className="text-gray-500 mt-1">
            Gérez les membres du personnel de votre établissement
          </p>
        </div>
        {permissions?.canCreateStaff && creatableStaffRoles.length > 0 && (
          campusCount === 0 ? (
            <Link
              to={campusManagementPath}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Building2 size={20} />
              <span>Créer un campus</span>
            </Link>
          ) : (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus size={20} />
              <span>Ajouter un membre</span>
            </button>
          )
        )}
      </div>

      {permissions?.canCreateStaff && campusCount === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <Building2 className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
          <div className="flex-1">
            <p className="text-blue-900 font-medium">
              Aucun campus n'est encore configuré pour votre établissement
            </p>
            <p className="text-blue-800 text-sm mt-1">
              Chaque membre du personnel doit être affecté à au moins un campus (notamment pour la paie).
              Commencez par créer un campus, puis revenez ici pour ajouter votre équipe.
            </p>
          </div>
        </div>
      )}

      {/* Success/Error Messages for resend email */}
      {resendSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <Mail className="text-green-600" size={20} />
          <p className="text-green-800">{resendSuccess}</p>
        </div>
      )}
      {resendError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <Mail className="text-red-600" size={20} />
          <p className="text-red-800">{resendError}</p>
        </div>
      )}

      {/* Dashboard Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Staff Card - Clickable */}
        {/* For MARKETING_MANAGER: Show "Team Marketing" instead of "Total Personnel" */}
        <button
          onClick={() => {
            setRoleFilter('ALL');
            setProfileStatusFilter('ALL');
            setShowChangeRequestsSection(false);
          }}
          className={`bg-white rounded-xl p-4 border-2 transition-all text-left ${
            roleFilter === 'ALL' && profileStatusFilter === 'ALL' && !showChangeRequestsSection
              ? 'border-primary-500 ring-2 ring-primary-200'
              : 'border-gray-200 hover:border-primary-300'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Users size={24} className="text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">
                {permissions?.currentRole === 'MARKETING_MANAGER' ? 'Team Marketing' : 'Total Personnel'}
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.totalStaff ?? (staff.length - archivedCount)}
              </p>
            </div>
          </div>
        </button>

        {/* Total Professors Card - Clickable - Hidden for MARKETING_MANAGER */}
        {permissions?.currentRole !== 'MARKETING_MANAGER' && (
          <button
            onClick={() => {
              setRoleFilter(roleFilter === 'TEACHERS' ? 'ALL' : 'TEACHERS');
              setProfileStatusFilter('ALL');
              setShowChangeRequestsSection(false);
            }}
            className={`bg-white rounded-xl p-4 border-2 transition-all text-left ${
              roleFilter === 'TEACHERS' && !showChangeRequestsSection
                ? 'border-purple-500 ring-2 ring-purple-200'
                : 'border-gray-200 hover:border-purple-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${roleFilter === 'TEACHERS' && !showChangeRequestsSection ? 'bg-purple-200' : 'bg-purple-100'}`}>
                <GraduationCap size={24} className="text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Professeurs</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats?.totalProfessors ?? 0}
                </p>
              </div>
            </div>
          </button>
        )}

        {/* Pending Validations Card - Clickable - Only shown if user can validate profiles */}
        {canValidateProfile && (
          <button
            onClick={() => {
              setProfileStatusFilter(profileStatusFilter === 'PENDING_VALIDATION' ? 'ALL' : 'PENDING_VALIDATION');
              setRoleFilter('ALL');
              setShowChangeRequestsSection(false);
            }}
            className={`bg-white rounded-xl p-4 border-2 transition-all text-left ${
              profileStatusFilter === 'PENDING_VALIDATION' && !showChangeRequestsSection
                ? 'border-orange-500 ring-2 ring-orange-200'
                : 'border-gray-200 hover:border-orange-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${pendingValidationCount > 0 ? 'bg-orange-100' : 'bg-gray-100'}`}>
                <Clock size={24} className={pendingValidationCount > 0 ? 'text-orange-600' : 'text-gray-400'} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Dossiers à valider</p>
                <p className={`text-2xl font-bold ${pendingValidationCount > 0 ? 'text-orange-600' : 'text-gray-900'}`}>
                  {pendingValidationCount}
                </p>
              </div>
            </div>
          </button>
        )}

        {/* Demandes de modification Card - Clickable - Only shown if user can validate modifications */}
        {canValidateModifications && (
          <button
            onClick={() => {
              setShowChangeRequestsSection(prev => !prev);
            }}
            className={`bg-white rounded-xl p-4 border-2 transition-all text-left ${
              showChangeRequestsSection
                ? 'border-blue-500 ring-2 ring-blue-200'
                : 'border-gray-200 hover:border-blue-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${pendingChangeRequests.length > 0 ? 'bg-blue-100' : 'bg-gray-100'}`}>
                <FileEdit size={24} className={pendingChangeRequests.length > 0 ? 'text-blue-600' : 'text-gray-400'} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Demandes de modification</p>
                <p className={`text-2xl font-bold ${pendingChangeRequests.length > 0 ? 'text-blue-600' : 'text-gray-900'}`}>
                  {pendingChangeRequests.length}
                </p>
              </div>
            </div>
          </button>
        )}
      </div>

      {/* Roles you can create - only shown if user has creation permissions */}
      {permissions?.canCreateStaff && creatableStaffRoles.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <span className="font-medium">Rôles que vous pouvez créer :</span>{' '}
            {creatableStaffRoles.map(r => r.nameFr).join(', ')}
          </p>
        </div>
      )}

      {/* Search and Filter Row */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Rechercher par nom ou email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {searching && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
          )}
        </div>

        {/* Profile Status Filter Dropdown */}
        <div className="relative">
          <button
            onClick={() => {
              const dropdown = document.getElementById('staff-profile-filter-dropdown');
              if (dropdown) dropdown.classList.toggle('hidden');
            }}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 min-w-[180px] justify-between"
          >
            <span className="flex items-center gap-2">
              <Filter size={16} className="text-gray-400" />
              <span className="text-gray-700">
                {profileStatusFilter === 'ALL' ? 'Statut profil' : profileStatusLabels[profileStatusFilter]?.label}
              </span>
            </span>
            <ChevronDown size={16} className="text-gray-400" />
          </button>
          <div
            id="staff-profile-filter-dropdown"
            className="hidden absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10"
          >
            {(['ALL', 'PENDING_VALIDATION', 'DRAFT', 'APPROVED', 'REJECTED'] as ProfileStatusFilter[]).map((status) => (
              <button
                key={status}
                onClick={() => {
                  setProfileStatusFilter(status);
                  document.getElementById('staff-profile-filter-dropdown')?.classList.add('hidden');
                }}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                  profileStatusFilter === status ? 'bg-gray-100 font-medium' : ''
                }`}
              >
                {status === 'ALL' ? 'Tous les statuts' : profileStatusLabels[status]?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Show Archived Staff Checkbox - Only show if user has archive permission */}
        {canArchiveStaff && archivedCount > 0 && (
          <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 cursor-pointer transition-colors">
            <input
              type="checkbox"
              checked={showArchivedStaff}
              onChange={(e) => setShowArchivedStaff(e.target.checked)}
              className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
            />
            <Archive size={16} className={showArchivedStaff ? 'text-orange-600' : 'text-gray-400'} />
            <span className={showArchivedStaff ? 'text-orange-800' : 'text-gray-700'}>
              Inclure archivés ({archivedCount})
            </span>
          </label>
        )}
      </div>

      {/* Advanced Filters Panel (checkboxes) */}
      <StaffFiltersPanel
        filters={advancedFilters}
        onFiltersChange={setAdvancedFilters}
        staffData={allStaff.length > 0 ? allStaff : staff}
        roleNames={roleNamesMap}
        availableAcademicYears={academicYears}
        academicYearCounts={academicYearCounts}
      />

      {/* Active Filter Badges */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-500">Filtres actifs:</span>

          {/* Role filter badge */}
          {roleFilter !== 'ALL' && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
              <GraduationCap size={14} />
              {roleFilter === 'TEACHERS' ? 'Professeurs' : getRoleName(roleFilter)}
              <button
                onClick={() => setRoleFilter('ALL')}
                className="ml-1 hover:bg-purple-200 rounded-full p-0.5"
              >
                <X size={14} />
              </button>
            </span>
          )}

          {/* Profile status filter badge */}
          {profileStatusFilter !== 'ALL' && (
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${profileStatusLabels[profileStatusFilter]?.color}`}>
              {React.createElement(profileStatusLabels[profileStatusFilter]?.icon, { size: 14 })}
              {profileStatusLabels[profileStatusFilter]?.label}
              <button
                onClick={() => setProfileStatusFilter('ALL')}
                className="ml-1 hover:bg-black/10 rounded-full p-0.5"
              >
                <X size={14} />
              </button>
            </span>
          )}

          {/* Advanced filters badges */}
          {advancedFilters.role && advancedFilters.role.length > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              <Users size={14} />
              {advancedFilters.role.length === 1
                ? roleNamesMap.get(advancedFilters.role[0]) || advancedFilters.role[0]
                : `${advancedFilters.role.length} rôles`}
              <button
                onClick={() => setAdvancedFilters({ ...advancedFilters, role: undefined })}
                className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
              >
                <X size={14} />
              </button>
            </span>
          )}

          {advancedFilters.academicYearId && advancedFilters.academicYearId.length > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <Calendar size={14} />
              {advancedFilters.academicYearId.length === 1
                ? (academicYears.find(ay => ay.id === advancedFilters.academicYearId![0])?.name ||
                   (currentAcademicYear?.id === advancedFilters.academicYearId[0] ? currentAcademicYear.name : advancedFilters.academicYearId[0]))
                : `${advancedFilters.academicYearId.length} années`}
              <button
                onClick={() => setAdvancedFilters({ ...advancedFilters, academicYearId: undefined })}
                className="ml-1 hover:bg-green-200 rounded-full p-0.5"
              >
                <X size={14} />
              </button>
            </span>
          )}

          {hasMultipleCampuses && advancedFilters.campusId && advancedFilters.campusId.length > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
              <Building2 size={14} />
              {advancedFilters.campusId.length === 1
                ? (allStaff.flatMap(s => s.campuses || []).find(c => c.id === advancedFilters.campusId![0])?.name || advancedFilters.campusId[0])
                : `${advancedFilters.campusId.length} campus`}
              <button
                onClick={() => setAdvancedFilters({ ...advancedFilters, campusId: undefined })}
                className="ml-1 hover:bg-amber-200 rounded-full p-0.5"
              >
                <X size={14} />
              </button>
            </span>
          )}

          <span className="text-sm text-gray-500">
            ({filteredStaff.length} affiché{filteredStaff.length > 1 ? 's' : ''}{totalStaffCount > filteredStaff.length ? ` · ${totalStaffCount} au total` : ''})
          </span>
        </div>
      )}

      {/* Admin Change Requests Section - Shown when toggled (replaces table and filters) */}
      {showChangeRequestsSection ? (
        <AdminChangeRequestsCard userTypeFilter="staff" onRequestReviewed={() => loadData()} />
      ) : (
      <>
      {/* Staff Table - Similar to Students Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rôle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                {hasMultipleCampuses && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campus
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                {(canManagePermissions || canEditProfile || canDeleteStaff || canArchiveStaff || canSuspendActivate || canResetPassword) && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredStaff.length === 0 ? (
                <tr>
                  <td colSpan={4 + (hasMultipleCampuses ? 1 : 0) + ((canManagePermissions || canEditProfile || canDeleteStaff || canArchiveStaff || canSuspendActivate || canResetPassword) ? 1 : 0)} className="px-6 py-12 text-center text-gray-500">
                    <UserCog className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                    {hasActiveFilters ? (
                      <>
                        <p>Aucun membre du personnel avec ces filtres</p>
                        <button
                          onClick={clearFilters}
                          className="text-sm text-primary-600 hover:underline mt-2"
                        >
                          Afficher tout le personnel
                        </button>
                      </>
                    ) : (
                      <>
                        <p>Aucun membre du personnel trouvé</p>
                        {permissions?.canCreateStaff && creatableStaffRoles.length > 0 && (
                          <p className="text-sm mt-2">
                            Cliquez sur "Ajouter un membre" pour créer un nouveau compte
                          </p>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ) : (
                filteredStaff.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    {/* Nom - Last name + First name (matches the alphabetical sort) */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {member.lastName} {member.firstName}
                      </div>
                    </td>

                    {/* Rôle */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {getRoleName(member.role)}
                      </span>
                    </td>

                    {/* Contact - Phone + Email */}
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        {member.phone && (
                          <div className="flex items-center gap-1 text-gray-900">
                            <Phone size={12} className="text-gray-400 flex-shrink-0" />
                            <span className="truncate">{formatPhoneWithCountry(member.phone, member.phoneCountry)}</span>
                          </div>
                        )}
                        {member.email && (
                          <div className="flex items-center gap-1 text-gray-500 text-xs">
                            <Mail size={12} className="text-gray-400 flex-shrink-0" />
                            <span className="truncate max-w-[180px]">{member.email}</span>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Campus - Only shown if multiple campuses exist */}
                    {hasMultipleCampuses && (
                      <td className="px-6 py-4">
                        {member.campuses && member.campuses.length > 0 ? (
                          <div className="flex items-center gap-1.5">
                            <Building2 size={12} className="text-gray-400 flex-shrink-0" />
                            <div className="text-sm text-gray-900 truncate max-w-[120px]" title={member.campuses.map(c => c.name).join(', ')}>
                              {member.campuses.find(c => c.isPrimary)?.name || member.campuses[0]?.name}
                              {member.campuses.length > 1 && (
                                <span className="text-gray-400 ml-1">(+{member.campuses.length - 1})</span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400 italic">Tous les sites</span>
                        )}
                      </td>
                    )}

                    {/* Statut - Account status + Profile status */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {/* Account status badge */}
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusLabels[member.status]?.color || 'bg-gray-100 text-gray-800'}`}>
                          {statusLabels[member.status]?.label || member.status}
                        </span>

                        {/* Resend activation email button for PENDING status */}
                        {canResendActivation && member.status === 'PENDING' && (
                          <button
                            onClick={() => handleResendEmail(member.id)}
                            disabled={resendingUserId === member.id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors disabled:opacity-50"
                            title="Renvoyer le lien d'activation"
                          >
                            {resendingUserId === member.id ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <RefreshCw size={12} />
                            )}
                            Renvoyer
                          </button>
                        )}

                        {/* Restart the submission deadline for profiles still to be submitted */}
                        {canValidateProfile && canValidateRole(currentUser?.role, member.role) && (member.profileSubmissionStatus === 'DRAFT' || member.profileSubmissionStatus === 'REJECTED') && member.id !== currentUser?.id && (
                          <button
                            onClick={() => setStaffToResetDeadline(member)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                            title="Réinitialiser le délai de soumission du profil"
                          >
                            <Hourglass size={12} />
                            Délai
                          </button>
                        )}

                        {/* Profile status badge */}
                        {member.profileSubmissionStatus && profileStatusLabels[member.profileSubmissionStatus] && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${profileStatusLabels[member.profileSubmissionStatus].color}`}>
                            {React.createElement(profileStatusLabels[member.profileSubmissionStatus].icon, { size: 10 })}
                            {profileStatusLabels[member.profileSubmissionStatus].label}
                          </span>
                        )}

                        {/* Validation button for PENDING_VALIDATION status - only shown if
                            the current user can validate THIS role (per hierarchy) */}
                        {canValidateProfile && canValidateRole(currentUser?.role, member.role) && member.profileSubmissionStatus === 'PENDING_VALIDATION' && member.id !== currentUser?.id && (
                          <button
                            onClick={() => handleOpenValidation(member.id)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 transition-colors"
                            title="Valider le profil"
                          >
                            <ClipboardCheck size={12} />
                            Valider
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Actions - Only show column if user has any action permissions */}
                    {(canManagePermissions || canEditProfile || canDeleteStaff || canArchiveStaff || canSuspendActivate || canResetPassword) && (
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          data-dropdown-trigger
                          ref={(el) => {
                            if (el) dropdownButtonRefs.current.set(member.id, el);
                          }}
                          onClick={(e) => handleOpenDropdown(member.id, e.currentTarget)}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Actions"
                        >
                          <MoreVertical size={18} />
                        </button>

                        {/* Dropdown Menu - Rendered via Portal */}
                        {openActionsDropdown === member.id && createPortal(
                          <div
                            ref={actionsDropdownRef}
                            className="fixed w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-[9999]"
                            style={{
                              top: dropdownPosition.showAbove ? 'auto' : dropdownPosition.top,
                              bottom: dropdownPosition.showAbove ? window.innerHeight - dropdownPosition.top + 4 : 'auto',
                              left: Math.max(8, dropdownPosition.left),
                            }}
                          >
                            {/* View Details */}
                            <button
                              onClick={() => {
                                handleViewStaff(member);
                                setOpenActionsDropdown(null);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              <Eye size={16} className="text-gray-400" />
                              Voir les details
                            </button>

                            {/* Edit Profile */}
                            {canEditProfile && (
                              <button
                                onClick={() => {
                                  handleEditProfileClick(member);
                                  setOpenActionsDropdown(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <Edit size={16} className="text-gray-400" />
                                Modifier le profil
                              </button>
                            )}

                            {/* Physical Documents — gated by the physical-docs
                                tracking permission (not edit-profile), so roles
                                like Coordinateur Académique that track physical
                                files can access it. */}
                            {canTrackPhysicalDocs && (
                              <button
                                onClick={() => {
                                  setStaffForPhysicalDocs(member);
                                  setShowPhysicalDocsModal(true);
                                  setOpenActionsDropdown(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <FolderOpen size={16} className="text-emerald-500" />
                                Documents physiques
                              </button>
                            )}

                            {/* Manage Permissions */}
                            {canManagePermissions && member.id !== currentUser?.id && (
                              <button
                                onClick={() => {
                                  handleOpenPermissions(member);
                                  setOpenActionsDropdown(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <Shield size={16} className="text-purple-500" />
                                Gerer les permissions
                              </button>
                            )}

                            {/* Reset Password */}
                            {canResetPassword && member.id !== currentUser?.id && (
                              <button
                                onClick={() => {
                                  handleResetPasswordClick(member);
                                  setOpenActionsDropdown(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <KeyRound size={16} className="text-blue-500" />
                                Reinitialiser mot de passe
                              </button>
                            )}

                            {/* Divider before dangerous actions */}
                            {(canSuspendActivate || canArchiveStaff || canDeleteStaff) && member.id !== currentUser?.id && (
                              <div className="border-t border-gray-100 my-1" />
                            )}

                            {/* Suspend/Activate */}
                            {canSuspendActivate && member.id !== currentUser?.id && member.status !== 'WITHDRAWN' && (
                              <button
                                onClick={() => {
                                  handleSuspendClick(member);
                                  setOpenActionsDropdown(null);
                                }}
                                className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                                  member.status === 'SUSPENDED'
                                    ? 'text-green-600 hover:bg-green-50'
                                    : 'text-orange-600 hover:bg-orange-50'
                                }`}
                              >
                                {member.status === 'SUSPENDED' ? (
                                  <>
                                    <UserPlus size={16} />
                                    Activer le compte
                                  </>
                                ) : (
                                  <>
                                    <Ban size={16} />
                                    Suspendre le compte
                                  </>
                                )}
                              </button>
                            )}

                            {/* Archive/Restore */}
                            {canArchiveStaff && member.id !== currentUser?.id && (
                              <button
                                onClick={() => {
                                  handleArchiveClick(member);
                                  setOpenActionsDropdown(null);
                                }}
                                className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                                  member.status === 'WITHDRAWN'
                                    ? 'text-green-600 hover:bg-green-50'
                                    : 'text-orange-600 hover:bg-orange-50'
                                }`}
                              >
                                {member.status === 'WITHDRAWN' ? (
                                  <>
                                    <ArchiveRestore size={16} />
                                    Restaurer
                                  </>
                                ) : (
                                  <>
                                    <Archive size={16} />
                                    Archiver (Demission)
                                  </>
                                )}
                              </button>
                            )}

                            {/* Delete */}
                            {canDeleteStaff && (
                              <button
                                onClick={() => {
                                  handleDeleteClick(member);
                                  setOpenActionsDropdown(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                              >
                                <Trash2 size={16} />
                                Supprimer
                              </button>
                            )}
                          </div>,
                          document.body
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Page {page} sur {totalPages} — {totalStaffCount} membre{totalStaffCount > 1 ? 's' : ''} du personnel
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>
      </>
      )}

      {/* Create Staff Modal */}
      <CreateStaffModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          loadData(); // Refresh the staff list
        }}
      />

      {/* View Staff Modal */}
      <ViewStaffModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedStaff(null);
        }}
        staff={selectedStaff}
        permissions={permissions}
      />

      {/* Profile Validation Modal */}
      {selectedValidationId && (
        <ProfileValidationModal
          isOpen={showValidationModal}
          onClose={() => {
            setShowValidationModal(false);
            setSelectedValidationId(null);
          }}
          validationRequestId={selectedValidationId}
          onValidationComplete={handleValidationComplete}
        />
      )}

      {/* Staff Permissions Modal */}
      {selectedStaffForPermissions && (
        <StaffPermissionsModal
          isOpen={showPermissionsModal}
          onClose={handleClosePermissions}
          staff={selectedStaffForPermissions}
          onPermissionsUpdated={() => {
            // Optionally refresh staff list or show success message
            loadData();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {staffToResetDeadline && (
        <ResetDeadlineModal
          user={staffToResetDeadline}
          isSubmitting={resettingDeadline}
          onConfirm={handleConfirmResetDeadline}
          onCancel={() => setStaffToResetDeadline(null)}
        />
      )}

      {showDeleteModal && staffToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCancelDelete}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
              {/* Close button */}
              <button
                onClick={handleCancelDelete}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>

              {/* Warning icon */}
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertTriangle size={32} className="text-red-600" />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Supprimer le compte
              </h3>

              {/* Staff info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-primary-100">
                    <UserCog size={20} className="text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {staffToDelete.firstName} {staffToDelete.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{staffToDelete.email}</p>
                  </div>
                </div>
              </div>

              {/* Warning message */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex gap-3">
                  <AlertCircle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">Attention : Cette action est irréversible</p>
                    <ul className="list-disc list-inside space-y-1 text-yellow-700">
                      <li>Toutes les données du personnel seront supprimées</li>
                      <li>L'historique et les documents seront perdus</li>
                      <li>Les permissions attribuées seront révoquées</li>
                    </ul>
                  </div>
                </div>
              </div>

              {deleteError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
                  <XCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{deleteError}</p>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleCancelDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Suppression...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      <span>Supprimer</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Archive/Restore Confirmation Modal */}
      {showArchiveModal && staffToArchive && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCancelArchive}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
              {/* Close button */}
              <button
                onClick={handleCancelArchive}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className={`p-3 rounded-full ${staffToArchive.status === 'WITHDRAWN' ? 'bg-green-100' : 'bg-orange-100'}`}>
                  {staffToArchive.status === 'WITHDRAWN' ? (
                    <ArchiveRestore size={32} className="text-green-600" />
                  ) : (
                    <Archive size={32} className="text-orange-600" />
                  )}
                </div>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                {staffToArchive.status === 'WITHDRAWN' ? 'Restaurer le compte' : 'Archiver le compte'}
              </h3>

              {/* Staff info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-primary-100">
                    <UserCog size={20} className="text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {staffToArchive.firstName} {staffToArchive.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{staffToArchive.email}</p>
                  </div>
                </div>
              </div>

              {/* Info message */}
              <div className={`border rounded-lg p-4 mb-6 ${
                staffToArchive.status === 'WITHDRAWN'
                  ? 'bg-green-50 border-green-200'
                  : 'bg-orange-50 border-orange-200'
              }`}>
                <div className="flex gap-3">
                  <AlertCircle size={20} className={`flex-shrink-0 mt-0.5 ${
                    staffToArchive.status === 'WITHDRAWN' ? 'text-green-600' : 'text-orange-600'
                  }`} />
                  <div className={`text-sm ${
                    staffToArchive.status === 'WITHDRAWN' ? 'text-green-800' : 'text-orange-800'
                  }`}>
                    {staffToArchive.status === 'WITHDRAWN' ? (
                      <>
                        <p className="font-medium mb-1">Restauration du compte</p>
                        <ul className="list-disc list-inside space-y-1 opacity-80">
                          <li>Le compte sera réactivé</li>
                          <li>L'utilisateur pourra se connecter à nouveau</li>
                          <li>Les données sont préservées</li>
                        </ul>
                      </>
                    ) : (
                      <>
                        <p className="font-medium mb-1">Archivage (Démission)</p>
                        <ul className="list-disc list-inside space-y-1 opacity-80">
                          <li>Le compte sera désactivé</li>
                          <li>L'utilisateur ne pourra plus se connecter</li>
                          <li>Les données sont conservées et peuvent être restaurées</li>
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleCancelArchive}
                  disabled={archiving}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmArchive}
                  disabled={archiving}
                  className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                    staffToArchive.status === 'WITHDRAWN'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-orange-600 hover:bg-orange-700'
                  }`}
                >
                  {archiving ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>{staffToArchive.status === 'WITHDRAWN' ? 'Restauration...' : 'Archivage...'}</span>
                    </>
                  ) : (
                    <>
                      {staffToArchive.status === 'WITHDRAWN' ? <ArchiveRestore size={16} /> : <Archive size={16} />}
                      <span>{staffToArchive.status === 'WITHDRAWN' ? 'Restaurer' : 'Archiver'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Suspend/Activate Confirmation Modal */}
      {showSuspendModal && staffToSuspend && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCancelSuspend}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
              {/* Close button */}
              <button
                onClick={handleCancelSuspend}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className={`p-3 rounded-full ${staffToSuspend.status === 'SUSPENDED' ? 'bg-green-100' : 'bg-red-100'}`}>
                  {staffToSuspend.status === 'SUSPENDED' ? (
                    <UserPlus size={32} className="text-green-600" />
                  ) : (
                    <Ban size={32} className="text-red-600" />
                  )}
                </div>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                {staffToSuspend.status === 'SUSPENDED' ? 'Activer le compte' : 'Suspendre le compte'}
              </h3>

              {/* Staff info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-primary-100">
                    <UserCog size={20} className="text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {staffToSuspend.firstName} {staffToSuspend.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{staffToSuspend.email}</p>
                  </div>
                </div>
              </div>

              {/* Suspension Info (when activating a suspended user) */}
              {staffToSuspend.status === 'SUSPENDED' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  {loadingSuspensionInfo ? (
                    <div className="flex items-center justify-center py-2">
                      <Loader2 size={20} className="animate-spin text-red-500" />
                      <span className="ml-2 text-sm text-red-600">Chargement...</span>
                    </div>
                  ) : suspensionInfo ? (
                    <div className="text-sm">
                      <p className="font-medium text-red-800 mb-2">Raison de la suspension:</p>
                      <p className="text-red-700 bg-red-100 p-2 rounded">
                        {suspensionInfo.suspensionReason || 'Aucune raison spécifiée'}
                      </p>
                      {suspensionInfo.suspendedBy && (
                        <p className="text-red-600 mt-2 text-xs">
                          Suspendu par: {suspensionInfo.suspendedBy.firstName} {suspensionInfo.suspendedBy.lastName}
                        </p>
                      )}
                      {suspensionInfo.suspendedAt && (
                        <p className="text-red-600 text-xs">
                          Le: {new Date(suspensionInfo.suspendedAt).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      )}
                    </div>
                  ) : null}
                </div>
              )}

              {/* Reason input (only for suspension, not activation) */}
              {staffToSuspend.status !== 'SUSPENDED' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Raison de la suspension <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={suspensionReason}
                    onChange={(e) => setSuspensionReason(e.target.value)}
                    placeholder="Indiquez la raison de la suspension..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                    maxLength={500}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Cette raison sera affichée au personnel lors de sa tentative de connexion.
                  </p>
                </div>
              )}

              {/* Error message */}
              {suspensionError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle size={16} />
                    <span className="text-sm">{suspensionError}</span>
                  </div>
                </div>
              )}

              {/* Info message */}
              <div className={`border rounded-lg p-4 mb-6 ${
                staffToSuspend.status === 'SUSPENDED'
                  ? 'bg-green-50 border-green-200'
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex gap-3">
                  <AlertCircle size={20} className={`flex-shrink-0 mt-0.5 ${
                    staffToSuspend.status === 'SUSPENDED' ? 'text-green-600' : 'text-red-600'
                  }`} />
                  <div className={`text-sm ${
                    staffToSuspend.status === 'SUSPENDED' ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {staffToSuspend.status === 'SUSPENDED' ? (
                      <>
                        <p className="font-medium mb-1">Réactivation du compte</p>
                        <ul className="list-disc list-inside space-y-1 opacity-80">
                          <li>Le compte sera réactivé</li>
                          <li>L'utilisateur pourra se connecter à nouveau</li>
                          <li>La raison de suspension sera effacée</li>
                        </ul>
                      </>
                    ) : (
                      <>
                        <p className="font-medium mb-1">Suspension du compte</p>
                        <ul className="list-disc list-inside space-y-1 opacity-80">
                          <li>Le compte sera temporairement bloqué</li>
                          <li>L'utilisateur ne pourra plus se connecter</li>
                          <li>La raison sera affichée lors de la tentative de connexion</li>
                          <li>Le compte peut être réactivé à tout moment</li>
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleCancelSuspend}
                  disabled={suspending}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmSuspend}
                  disabled={suspending || (staffToSuspend.status !== 'SUSPENDED' && !suspensionReason.trim())}
                  className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                    staffToSuspend.status === 'SUSPENDED'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {suspending ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>{staffToSuspend.status === 'SUSPENDED' ? 'Activation...' : 'Suspension...'}</span>
                    </>
                  ) : (
                    <>
                      {staffToSuspend.status === 'SUSPENDED' ? <UserPlus size={16} /> : <Ban size={16} />}
                      <span>{staffToSuspend.status === 'SUSPENDED' ? 'Activer' : 'Suspendre'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Choice Modal - Email or Temp Password */}
      {showResetPasswordChoiceModal && staffForPasswordReset && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCancelPasswordChoice}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
              {/* Close button */}
              <button
                onClick={handleCancelPasswordChoice}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <KeyRound size={32} className="text-blue-600" />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Réinitialiser le mot de passe
              </h3>

              {/* Staff info */}
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-primary-100">
                    <UserCog size={20} className="text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {staffForPasswordReset.firstName} {staffForPasswordReset.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{staffForPasswordReset.email}</p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-500 text-center mb-4">
                Choisissez la méthode de réinitialisation
              </p>

              {/* Options */}
              <div className="space-y-2">
                {/* Option 1: Email */}
                <button
                  onClick={handleChooseStaffEmailReset}
                  className="w-full p-3 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left flex items-center gap-3"
                >
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-blue-100">
                    <Mail size={20} className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 text-sm">Envoyer un email</p>
                    <p className="text-xs text-gray-400">
                      Un lien de réinitialisation sera envoyé par email
                    </p>
                  </div>
                </button>

                {/* Option 2: Temp password */}
                <button
                  onClick={handleChooseStaffTempPasswordReset}
                  className="w-full p-3 border border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors text-left flex items-center gap-3"
                >
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-green-100">
                    <Key size={20} className="text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 text-sm">Mot de passe temporaire</p>
                    <p className="text-xs text-gray-400">
                      Créer un mot de passe à partager avec le membre du personnel
                    </p>
                  </div>
                </button>
              </div>

              {/* Cancel button */}
              <button
                onClick={handleCancelPasswordChoice}
                className="w-full mt-4 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Staff Temp Password Reset Modal */}
      {showResetStaffTempPasswordModal && staffToResetPassword && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCancelResetStaffTempPassword}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
              {/* Close button */}
              <button
                onClick={handleCancelResetStaffTempPassword}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <KeyRound size={32} className="text-green-600" />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Mot de passe temporaire - Personnel
              </h3>

              {/* Staff info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-primary-100">
                    <UserCog size={20} className="text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {staffToResetPassword.firstName} {staffToResetPassword.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{staffToResetPassword.email}</p>
                  </div>
                </div>
              </div>

              {/* Error message */}
              {resetStaffTempPasswordError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex gap-3">
                    <AlertCircle size={20} className="text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-800">{resetStaffTempPasswordError}</p>
                  </div>
                </div>
              )}

              {/* Success - Show credentials */}
              {staffTempResetResult && (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex gap-3">
                      <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
                      <p className="text-sm text-green-800">
                        Mot de passe temporaire créé avec succès
                      </p>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-4">
                    Partagez ces identifiants avec le membre du personnel. Il devra changer son mot de passe à la première connexion.
                  </p>

                  {/* Credentials */}
                  <div className="space-y-3">
                    {/* Email */}
                    <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <div>
                        <span className="text-xs text-gray-500 block">Email</span>
                        <span className="font-mono font-medium text-gray-900 text-sm">{staffTempResetResult.email}</span>
                      </div>
                      <button
                        onClick={() => staffTempResetResult.email && copyStaffCredential(staffTempResetResult.email, 'email')}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                        title="Copier"
                      >
                        {copiedStaffField === 'email' ? (
                          <CheckCircle size={18} className="text-green-600" />
                        ) : (
                          <Copy size={18} />
                        )}
                      </button>
                    </div>

                    {/* Temp Password */}
                    <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <div className="flex-1">
                        <span className="text-xs text-gray-500 block">Mot de passe temporaire</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium text-gray-900">
                            {showStaffTempPassword ? staffTempResetResult.tempPassword : '••••••••'}
                          </span>
                          <button
                            onClick={() => setShowStaffTempPassword(!showStaffTempPassword)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                            title={showStaffTempPassword ? 'Masquer' : 'Afficher'}
                          >
                            {showStaffTempPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        </div>
                      </div>
                      <button
                        onClick={() => copyStaffCredential(staffTempResetResult.tempPassword, 'password')}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                        title="Copier"
                      >
                        {copiedStaffField === 'password' ? (
                          <CheckCircle size={18} className="text-green-600" />
                        ) : (
                          <Copy size={18} />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Warning */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                    <div className="flex gap-2">
                      <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800">
                        Ce mot de passe temporaire ne sera plus visible après la fermeture de cette fenêtre.
                        Assurez-vous de le noter ou de le communiquer au membre du personnel.
                      </p>
                    </div>
                  </div>

                  {/* Close button */}
                  <button
                    onClick={handleCancelResetStaffTempPassword}
                    className="w-full mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Fermer
                  </button>
                </>
              )}

              {/* Before reset - Info message and buttons */}
              {!staffTempResetResult && (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex gap-3">
                      <AlertCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-green-800">
                        <p className="font-medium mb-1">Un mot de passe temporaire sera créé</p>
                        <ul className="list-disc list-inside space-y-1 text-green-700">
                          <li>Un nouveau mot de passe temporaire sera généré</li>
                          <li>Le membre du personnel devra le changer à la première connexion</li>
                          <li>Vous pourrez copier les identifiants pour les partager</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={handleCancelResetStaffTempPassword}
                      disabled={resettingStaffTempPassword}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleConfirmResetStaffTempPassword}
                      disabled={resettingStaffTempPassword}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {resettingStaffTempPassword ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          <span>Création...</span>
                        </>
                      ) : (
                        <>
                          <Key size={16} />
                          <span>Créer le mot de passe</span>
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Email Confirmation Modal */}
      {showResetPasswordModal && staffToResetPassword && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCancelResetPassword}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
              {/* Close button */}
              <button
                onClick={handleCancelResetPassword}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <KeyRound size={32} className="text-blue-600" />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Réinitialiser le mot de passe
              </h3>

              {/* Staff info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-primary-100">
                    <UserCog size={20} className="text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {staffToResetPassword.firstName} {staffToResetPassword.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{staffToResetPassword.email}</p>
                  </div>
                </div>
              </div>

              {/* Success message */}
              {resetPasswordSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <div className="flex gap-3">
                    <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
                    <p className="text-sm text-green-800">{resetPasswordSuccess}</p>
                  </div>
                </div>
              )}

              {/* Error message */}
              {resetPasswordError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex gap-3">
                    <AlertCircle size={20} className="text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-800">{resetPasswordError}</p>
                  </div>
                </div>
              )}

              {/* Info message */}
              {!resetPasswordSuccess && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex gap-3">
                    <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Un email sera envoyé au membre du personnel</p>
                      <ul className="list-disc list-inside space-y-1 text-blue-700">
                        <li>Un lien de réinitialisation sera envoyé par email</li>
                        <li>Le lien sera valide pendant 24 heures</li>
                        <li>Il devra définir un nouveau mot de passe</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Buttons */}
              {!resetPasswordSuccess && (
                <div className="flex gap-3">
                  <button
                    onClick={handleCancelResetPassword}
                    disabled={resettingPassword}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleConfirmResetPassword}
                    disabled={resettingPassword}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {resettingPassword ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        <span>Envoi...</span>
                      </>
                    ) : (
                      <>
                        <Mail size={16} />
                        <span>Envoyer le lien</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {staffToEdit && (
        <DirectEditStaffProfileModal
          isOpen={showEditProfileModal}
          onClose={() => {
            setShowEditProfileModal(false);
            setStaffToEdit(null);
          }}
          staffUserId={staffToEdit.id}
          staffName={`${staffToEdit.firstName} ${staffToEdit.lastName}`}
          onSuccess={handleEditProfileSuccess}
        />
      )}

      {/* Physical Documents Tracking Modal */}
      {staffForPhysicalDocs && (
        <PhysicalDocumentsTrackingModal
          isOpen={showPhysicalDocsModal}
          onClose={() => {
            setShowPhysicalDocsModal(false);
            setStaffForPhysicalDocs(null);
          }}
          staff={staffForPhysicalDocs}
          onSuccess={() => {
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default StaffManagementPage;
